import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { PenLine, ArrowLeft } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ThreadCard from "@/components/ThreadCard";

const field = "w-full rounded-sm border border-[#2A2E37] bg-white/5 px-4 py-2.5 text-sm outline-none focus:border-white/40";

function NewThread({ slug, onCreated }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", about_username: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/communities/${slug}/threads`, { ...form, about_username: form.about_username || null });
      onCreated(data);
      setOpen(false);
      setForm({ title: "", body: "", about_username: "" });
    } catch (err) { toast.error(formatErr(err)); }
  };
  return (
    <>
      <button onClick={() => (user ? setOpen(true) : nav("/login"))} data-testid="new-thread-btn"
        className="btn-neon flex items-center gap-2 rounded-sm px-5 py-2.5 text-sm font-semibold text-white"><PenLine className="h-4 w-4" /> New post</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-md border-[#2A2E37] bg-[#121418] text-white" data-testid="new-thread-dialog">
          <DialogHeader><DialogTitle className="font-display text-lg font-semibold">Post to c/{slug}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <input className={field} placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="thread-title-input" />
            <textarea className={`${field} min-h-[120px]`} placeholder="Say more (optional)" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="thread-body-input" />
            <input className={field} placeholder="About a creator? @username (optional)" value={form.about_username}
              onChange={(e) => setForm({ ...form, about_username: e.target.value })} data-testid="thread-about-input" />
            <button type="submit" data-testid="thread-submit-btn" className="btn-neon w-full rounded-sm py-3 font-semibold text-white">Post</button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function CommunityPage() {
  const { slug } = useParams();
  const [community, setCommunity] = useState(null);
  const [threads, setThreads] = useState([]);
  const [sort, setSort] = useState("hot");

  useEffect(() => { api.get(`/communities/${slug}`).then((r) => setCommunity(r.data)).catch(() => setCommunity(false)); }, [slug]);
  useEffect(() => { api.get("/threads", { params: { community: slug, sort } }).then((r) => setThreads(r.data)).catch(() => {}); }, [slug, sort]);

  if (community === false) return <div className="p-16 text-center text-gray-400" data-testid="community-not-found">Community not found.</div>;
  if (!community) return <div className="p-16 text-center text-gray-500">Loading...</div>;

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 md:px-8 md:py-14" data-testid="community-page">
      <Link to="/c" className="flex items-center gap-1 text-sm text-gray-400 hover:text-white" data-testid="back-to-communities"><ArrowLeft className="h-4 w-4" /> Communities</Link>
      <div className="mt-6 flex flex-col gap-4 rounded-md border border-[#2A2E37] bg-[#121418] p-6 md:flex-row md:items-center md:justify-between md:p-8">
        <div>
          <p className="text-sm font-semibold text-[#FF2E4D]">c/{community.slug}</p>
          <h1 className="font-display text-xl font-semibold tracking-tight sm:text-2xl" data-testid="community-name">{community.name}</h1>
          <p className="mt-2 text-sm text-gray-300">{community.description}</p>
        </div>
        <NewThread slug={slug} onCreated={(t) => setThreads((x) => [t, ...x])} />
      </div>
      <Tabs value={sort} onValueChange={setSort} className="mt-8">
        <TabsList className="rounded-sm border border-[#2A2E37] bg-[#121418] p-1">
          <TabsTrigger value="hot" className="rounded-sm px-5 data-[state=active]:bg-white/10 data-[state=active]:text-white" data-testid="sort-hot">Hot</TabsTrigger>
          <TabsTrigger value="new" className="rounded-sm px-5 data-[state=active]:bg-white/10 data-[state=active]:text-white" data-testid="sort-new">New</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="mt-6 space-y-4" data-testid="community-threads">
        {threads.map((t, i) => <ThreadCard key={t.id} thread={t} index={i} />)}
        {threads.length === 0 && <p className="text-sm text-gray-500">No posts yet. Start the conversation.</p>}
      </div>
    </div>
  );
}
